import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Link,
  IconButton,
  Tooltip,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import type {
  CatalogueProduct,
  CatalogueCategory,
  ProductWording,
  CatalogueCustomSection,
} from '@traaker/shared';
import { renderMarkdown } from './highlight';
import { ScoreHistorySparkline } from './ScoreHistorySparkline';

/** A GFM table row (`| a | b |`) anywhere in the text — the preview-toggle trigger (item c). */
const HAS_TABLE_ROW = /^\|.+\|$/m;

// Fixed before/after sections (each has a CURRENT page value → keep/optimize).
// The set differs by page type: a PRODUCT has the 6 GEO sections; a CATEGORY
// (product-listing page) has two editorial paragraphs around the product grid.
type FixedKey =
  | 'usp'
  | 'description'
  | 'specs'
  | 'shopperIntent'
  | 'competitiveness'
  | 'faq'
  | 'topParagraph'
  | 'bottomParagraph';
const PRODUCT_FIELDS: { key: FixedKey; label: string; rows: number }[] = [
  { key: 'usp', label: 'Points forts (USP)', rows: 2 },
  { key: 'description', label: 'Description', rows: 4 },
  { key: 'specs', label: 'Spécifications & chiffres', rows: 3 },
  { key: 'shopperIntent', label: "Intention d'achat / usages", rows: 2 },
  { key: 'competitiveness', label: 'Compétitivité', rows: 2 },
  { key: 'faq', label: 'FAQ', rows: 3 },
];
const CATEGORY_FIELDS: { key: FixedKey; label: string; rows: number }[] = [
  { key: 'topParagraph', label: 'Paragraphe haut de page (au-dessus des produits)', rows: 4 },
  { key: 'bottomParagraph', label: 'Paragraphe bas de page (sous la liste)', rows: 4 },
];

type Choice = 'keep' | 'optimize';

/** HTML-escape for a safe `content="..."` attribute value. */
function escapeHtmlAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** The real, copy-pastable `<title>` + meta description tags (item d). */
function seoHtmlTags(seoTitle: string, seoMeta: string): string {
  const lines: string[] = [];
  if (seoTitle.trim()) lines.push(`<title>${escapeHtmlAttr(seoTitle.trim())}</title>`);
  if (seoMeta.trim()) lines.push(`<meta name="description" content="${escapeHtmlAttr(seoMeta.trim())}">`);
  return lines.join('\n');
}

const EMPTY: ProductWording = {
  usp: '',
  description: '',
  specs: '',
  shopperIntent: '',
  competitiveness: '',
  faq: '',
  topParagraph: '',
  bottomParagraph: '',
};

/**
 * Per-section before/after editor. Shows the CURRENT page content (struck-through
 * where the optimized version replaces it) beside the editable GEO rewrite, and
 * lets the user choose per section: KEEP the current text, or use the OPTIMIZED
 * one. The saved wording = the per-section choice (edited optimized text when
 * "optimize", the original when "keep").
 */
export function WordingEditorDialog({
  open,
  item,
  customSections,
  disabledSections,
  onClose,
  onSave,
}: {
  open: boolean;
  item: CatalogueProduct | CatalogueCategory;
  customSections?: CatalogueCustomSection[];
  /** Fixed sections the catalogue has disabled (item b) — hidden from the editor. */
  disabledSections?: string[];
  onClose: () => void;
  onSave: (wording: ProductWording) => Promise<void>;
}) {
  const original: ProductWording = { ...EMPTY, ...(item.originalWording || {}) };
  const optimized: ProductWording = { ...EMPTY, ...(item.wording || {}) };
  const sections = (customSections || []).filter((s) => s.key && s.label);
  // Product vs. category page → different fixed section set, minus any the
  // catalogue disabled (item b).
  const disabled = new Set(disabledSections || []);
  const FIELDS = (item.type === 'category' ? CATEGORY_FIELDS : PRODUCT_FIELDS).filter(
    (f) => !disabled.has(f.key),
  );

  // Editable optimized text (user can tweak the rewrite before saving).
  const [draft, setDraft] = useState<ProductWording>(optimized);
  // Editable SEO tags + custom sections.
  const [seoTitle, setSeoTitle] = useState(optimized.seoTitle || '');
  const [seoMeta, setSeoMeta] = useState(optimized.seoMetaDescription || '');
  const [customDraft, setCustomDraft] = useState<Record<string, string>>(() => ({ ...(optimized.custom || {}) }));
  // Per-section choice; default "optimize" where a rewrite exists, else "keep".
  const [choice, setChoice] = useState<Record<FixedKey, Choice>>(() => {
    const c = {} as Record<FixedKey, Choice>;
    for (const f of FIELDS) c[f.key] = optimized[f.key]?.trim() ? 'optimize' : 'keep';
    return c;
  });
  const [saving, setSaving] = useState(false);
  // Per-section preview mode (item c): "Aperçu" renders GFM tables/markdown;
  // "Markdown" is the raw editable TextField (the copy-to-CMS source of
  // truth). Defaults to Aperçu when the optimized text contains a table.
  const [previewMode, setPreviewMode] = useState<Record<string, boolean>>(() => {
    const m: Record<string, boolean> = {};
    for (const f of FIELDS) m[f.key] = HAS_TABLE_ROW.test(optimized[f.key] || '');
    return m;
  });

  const href = item.urlNormalized.startsWith('http')
    ? item.urlNormalized
    : `https://${item.urlNormalized}`;

  /** The text that will actually be saved for a fixed section, per its choice. */
  const resolved = (key: FixedKey): string =>
    (choice[key] === 'keep' ? original[key] : draft[key]) || '';

  const copyAll = () => {
    const parts = FIELDS.map((f) => `## ${f.label}\n${resolved(f.key)}`);
    if (seoTitle) parts.push(`## SEO Title\n${seoTitle}`);
    if (seoMeta) parts.push(`## SEO Meta description\n${seoMeta}`);
    for (const s of sections) if (customDraft[s.key]) parts.push(`## ${s.label}\n${customDraft[s.key]}`);
    const htmlTags = seoHtmlTags(seoTitle, seoMeta);
    if (htmlTags) parts.push(`## Balises HTML\n${htmlTags}`);
    navigator.clipboard.writeText(parts.join('\n\n'));
  };

  const hasOriginal = FIELDS.some((f) => original[f.key]?.trim());

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <span>Réécriture GEO — {item.name || item.urlNormalized}</span>
          {item.geoScore != null && (
            <Typography variant="caption" color="text.secondary">
              Score GEO {item.geoScore}
            </Typography>
          )}
          <ScoreHistorySparkline history={item.scoreHistory} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        {item.analysisSummary && (
          <Alert severity="info" icon={false} sx={{ mb: 2, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Analyse
            </Typography>
            <Typography variant="body2">{item.analysisSummary}</Typography>
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          Pour chaque section : gardez le contenu <strong>actuel</strong> de la page ou adoptez
          la <strong>réécriture GEO</strong> (modifiable). Copiez le résultat dans votre site pour
          améliorer le score.{' '}
          <Link href={href} target="_blank" rel="noopener">
            Ouvrir la page <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
          </Link>
        </Alert>

        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {FIELDS.map((f) => {
            const cur = original[f.key]?.trim() || '';
            const opt = draft[f.key] || '';
            const ch = choice[f.key];
            return (
              <Box key={f.key}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {f.label}
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={ch}
                    onChange={(_, v) => v && setChoice((c) => ({ ...c, [f.key]: v }))}
                  >
                    <ToggleButton value="keep" sx={{ py: 0.25, px: 1 }}>
                      Actuel
                    </ToggleButton>
                    <ToggleButton value="optimize" sx={{ py: 0.25, px: 1 }}>
                      Optimisé
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                {/* CURRENT (before) — struck through when the optimized one is chosen. */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    mb: 1,
                    bgcolor: ch === 'keep' ? 'rgba(46,125,50,0.08)' : 'action.hover',
                    borderColor: ch === 'keep' ? 'success.main' : 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Actuel {ch === 'keep' && '· conservé'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      textDecoration: ch === 'optimize' && cur ? 'line-through' : 'none',
                      color: ch === 'optimize' && cur ? 'text.disabled' : 'text.primary',
                      fontStyle: cur ? 'normal' : 'italic',
                    }}
                  >
                    {cur || '(vide sur la page)'}
                  </Typography>
                </Paper>

                {/* OPTIMIZED (after) — editable; disabled/greyed when "keep". A
                    detected GFM table gets an Aperçu/Markdown toggle (item c):
                    the TextField stays the source of truth for editing/copy,
                    Aperçu is a read-only rendered view for readability. */}
                {HAS_TABLE_ROW.test(opt) && (
                  <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={previewMode[f.key] ? 'preview' : 'markdown'}
                      onChange={(_, v) => v && setPreviewMode((m) => ({ ...m, [f.key]: v === 'preview' }))}
                    >
                      <ToggleButton value="preview" sx={{ py: 0.125, px: 0.75 }}>
                        <VisibilityIcon sx={{ fontSize: 14, mr: 0.5 }} /> Aperçu
                      </ToggleButton>
                      <ToggleButton value="markdown" sx={{ py: 0.125, px: 0.75 }}>
                        <CodeIcon sx={{ fontSize: 14, mr: 0.5 }} /> Markdown
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}
                {previewMode[f.key] && HAS_TABLE_ROW.test(opt) ? (
                  <Paper variant="outlined" sx={{ p: 1.5, fontSize: '0.875rem' }}>
                    {renderMarkdown(opt, [])}
                  </Paper>
                ) : (
                  <TextField
                    value={opt}
                    onChange={(e) => setDraft((w) => ({ ...w, [f.key]: e.target.value }))}
                    multiline
                    minRows={f.rows}
                    fullWidth
                    size="small"
                    disabled={ch === 'keep'}
                    label="Réécriture GEO"
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Copier">
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(resolved(f.key))}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                )}
              </Box>
            );
          })}

          {/* SEO tags — editable, no before/after (they're generated, not on-page). */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Balises SEO
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                fullWidth
                size="small"
                label="Title SEO"
                helperText={`${seoTitle.length} / ~60 caractères`}
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Copier">
                      <IconButton size="small" onClick={() => navigator.clipboard.writeText(seoTitle)}>
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <TextField
                value={seoMeta}
                onChange={(e) => setSeoMeta(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                size="small"
                label="Meta description SEO"
                helperText={`${seoMeta.length} / ~155 caractères`}
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Copier">
                      <IconButton
                        size="small"
                        onClick={() => navigator.clipboard.writeText(seoMeta)}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Stack>

            {/* Real copy-pastable HTML tags (item d) — updates live as the
                fields change. Escapes quotes so the attribute stays valid. */}
            {(seoTitle.trim() || seoMeta.trim()) && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: 'action.hover',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  position: 'relative',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => navigator.clipboard.writeText(seoHtmlTags(seoTitle, seoMeta))}
                  sx={{ position: 'absolute', top: 4, right: 4 }}
                  aria-label="Copier les balises HTML"
                >
                  <ContentCopyIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', pr: 4 }}>
                  {seoHtmlTags(seoTitle, seoMeta)}
                </Box>
              </Paper>
            )}
          </Box>

          {/* User-defined custom sections (from the catalogue config). */}
          {sections.map((s) => (
            <Box key={s.key}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {s.label}
              </Typography>
              <TextField
                value={customDraft[s.key] || ''}
                onChange={(e) => setCustomDraft((c) => ({ ...c, [s.key]: e.target.value }))}
                multiline
                minRows={2}
                fullWidth
                size="small"
                label="Réécriture GEO"
                placeholder={s.instruction}
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Copier">
                      <IconButton
                        size="small"
                        onClick={() => navigator.clipboard.writeText(customDraft[s.key] || '')}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Box>
          ))}
        </Stack>
        {!hasOriginal && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Le contenu actuel n'a pas pu être extrait de la page — seule la réécriture est
            disponible pour comparaison.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<ContentCopyIcon />} onClick={copyAll} disabled={saving}>
          Copier tout
        </Button>
        <Button onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={async () => {
            setSaving(true);
            try {
              const merged = {} as ProductWording;
              for (const f of FIELDS) merged[f.key] = resolved(f.key);
              if (seoTitle.trim()) merged.seoTitle = seoTitle.trim();
              if (seoMeta.trim()) merged.seoMetaDescription = seoMeta.trim();
              const custom: Record<string, string> = {};
              for (const s of sections) if ((customDraft[s.key] || '').trim()) custom[s.key] = customDraft[s.key].trim();
              if (Object.keys(custom).length) merged.custom = custom;
              await onSave(merged);
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
